import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  Eye,
  Edit,
  Trash2,
  FileText,
  CheckCircle,
  Filter,
  Download,
} from 'lucide-react';
import { useCompras, useDeleteCompra, useGenerateCompraPDF } from '../../hooks/useCompras';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate, getFileUrl } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../api/axios'; // <-- Asegúrate de que esta ruta apunte a tu instancia configurada de Axios

type EstadoCompra = 'todas' | 'pendiente' | 'enviada' | 'recibida' | 'cancelada';

const ESTADOS: { value: EstadoCompra; label: string; color: string }[] = [
  { value: 'todas', label: 'Todas', color: 'gray' },
  { value: 'pendiente', label: 'Pendiente', color: 'yellow' },
  { value: 'enviada', label: 'Enviada', color: 'blue' },
  { value: 'recibida', label: 'Recibida', color: 'green' },
  { value: 'cancelada', label: 'Cancelada', color: 'red' },
];

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    enviada: { variant: 'info', label: 'Enviada' },
    recibida: { variant: 'success', label: 'Recibida' },
    cancelada: { variant: 'danger', label: 'Cancelada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

export function CompraList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoCompra>('todas');
  const [fechaInicioLocal, setFechaInicioLocal] = useState('');
  const [fechaFinLocal, setFechaFinLocal] = useState('');

  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  
  const handleFechaInicioChange = (value: string) => {
    setFechaInicioLocal(value);
    const timeout = setTimeout(() => setFechaInicio(value), 500);
    return () => clearTimeout(timeout);
  };

  const handleFechaFinChange = (value: string) => {
    setFechaFinLocal(value);
    const timeout = setTimeout(() => setFechaFin(value), 500);
    return () => clearTimeout(timeout);
  };

  const params: any = {};
  if (estadoFiltro !== 'todas') params.estado = estadoFiltro;
  if (fechaInicio) params.fechaInicio = fechaInicio;
  if (fechaFin) params.fechaFin = fechaFin;

  const { data: compras, isLoading, isError, refetch } = useCompras(params);
  const deleteCompra = useDeleteCompra();
  const generatePDF = useGenerateCompraPDF();

  const comprasData = compras || [];
  const comprasFiltradas = comprasData.filter((compra: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    const proveedorNombre = typeof compra.proveedor === 'object' 
      ? compra.proveedor?.nombre || '' 
      : compra.proveedor || '';
    
    return (
      compra.numeroCompra?.toLowerCase().includes(searchLower) ||
      proveedorNombre.toLowerCase().includes(searchLower) ||
      compra.observaciones?.toLowerCase().includes(searchLower)
    );
  });

  const handleDelete = async (id: number, numero: string, estado: string) => {
    if (estado !== 'pendiente' && estado !== 'cancelada' && estado !== 'recibida') {
      toast.warning('Solo se pueden eliminar compras en estado pendiente, recibida o cancelada');
      return;
    }

    const mensajeAdicional = estado === 'recibida' 
      ? ' (Esto también descontará el stock)' 
      : '';

    if (!confirm(`¿Estás seguro de eliminar la compra ${numero}?${mensajeAdicional}`)) return;

    try {
      await deleteCompra.mutateAsync(id);
      refetch();
    } catch (error: any) {
      // Error ya manejado por react-query
    }
  };

  // Función segura para descargar el PDF pasando el Token por Render
  const handleDescargarPDF = async (pdfUrl: string, numeroCompra: string) => {
    try {
      const toastId = toast.loading('Descargando PDF...');
      const urlCompleta = getFileUrl(pdfUrl);
      
      const response = await api.get(urlCompleta, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${numeroCompra || 'compra'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);
      
      toast.dismiss(toastId);
      toast.success('Descargado correctamente');
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error('Error al descargar el PDF');
    }
  };

  const handleGeneratePDF = async (id: number) => {
    try {
      const result = await generatePDF.mutateAsync(id);
      if (result?.pdfUrl) {
        toast.success('Generado exitosamente');
        refetch();
        handleDescargarPDF(result.pdfUrl, result.numeroCompra || 'compra');
      }
    } catch (error: any) {
      console.error(error);
      toast.error('Error al generar el PDF');
    }
  };

  const handleLimpiarFiltros = () => {
    setSearchTerm('');
    setEstadoFiltro('todas');
    setFechaInicioLocal('');
    setFechaFinLocal('');
    setFechaInicio('');
    setFechaFin('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando compras...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar las compras
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Compras</h1>
          <p className="text-sm text-gray-500">
            {comprasFiltradas.length} compra(s) encontradas
          </p>
        </div>
        <Link to="/compras/nuevo">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={18} />
            Nueva Compra
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por número o proveedor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 min-w-[180px]">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value as EstadoCompra)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
            >
              {ESTADOS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleLimpiarFiltros}
            className="text-gray-500"
          >
            <Filter size={16} className="mr-2" />
            Limpiar
          </Button>
        </div>

        {/* Filtros de fecha */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha desde</label>
            <input
              type="date"
              value={fechaInicioLocal}
              onChange={(e) => handleFechaInicioChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha hasta</label>
            <input
              type="date"
              value={fechaFinLocal}
              onChange={(e) => handleFechaFinChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Lista de compras */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {comprasFiltradas.map((compra: any) => (
          <div key={compra.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-blue-500" />
                  <h3 className="font-semibold text-gray-800">{compra.numeroCompra}</h3>
                  {getEstadoBadge(compra.estado)}
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {typeof compra.proveedor === 'object' 
                    ? compra.proveedor?.nombre || 'Sin proveedor'
                    : compra.proveedor || 'Sin proveedor'}
                </p>
              </div>
              <div className="flex gap-1">
                <Link
                  to={`/compras/${compra.id}`}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                >
                  <Eye size={16} />
                </Link>
                {compra.estado === 'pendiente' && (
                  <Link
                    to={`/compras/editar/${compra.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                )}
                {(compra.estado === 'pendiente' || compra.estado === 'cancelada' || compra.estado === 'recibida') && (
                  <button
                    onClick={() => handleDelete(compra.id, compra.numeroCompra, compra.estado)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Detalles */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Productos</span>
                <span className="font-medium text-gray-700">
                  {compra.detalles?.length || 0} productos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-semibold text-gray-800">
                  {formatCurrency(compra.total || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha</span>
                <span className="text-xs text-gray-400">
                  {formatDate(compra.fechaCompra || compra.createdAt)}
                </span>
              </div>
              {compra.fechaEntrega && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Entrega</span>
                  <span className="text-xs text-gray-400">
                    {new Date(compra.fechaEntrega).toLocaleString('es-AR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
              {!compra.pdfUrl ? (
                <button
                  onClick={() => handleGeneratePDF(compra.id)}
                  className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                >
                  <Download size={14} />
                  Generar PDF
                </button>
              ) : (
                <button
                  onClick={() => handleDescargarPDF(compra.pdfUrl, compra.numeroCompra)}
                  className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                >
                  <Download size={14} />
                  Descargar
                </button>
              )}
              {compra.estado === 'pendiente' && (
                <Link
                  to={`/compras/${compra.id}`}
                  className="flex-1 flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                >
                  <CheckCircle size={14} />
                  Gestionar
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {comprasFiltradas.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron compras</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay compras que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/compras/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear la primera compra
          </Link>
        </div>
      )}
    </div>
  );
}